import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import offlineStorage from '../utils/offlineStorage';
import syncService from '../utils/syncService';

const ONLINE_SAVE_TIMEOUT_MS = 4000;

export const useOffline = (token = null) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineMatches, setOfflineMatches] = useState(() => offlineStorage.getOfflineMatches());
  const [syncStatus, setSyncStatus] = useState({
    isSyncing: false,
    unsyncedCount: 0,
    failedCount: 0,
    lastSyncMessage: '',
    canSync: false
  });

  useEffect(() => {
    const unsubscribeNetwork = offlineStorage.onNetworkChange((status) => {
      setIsOnline(status === 'online');
    });
    return unsubscribeNetwork;
  }, []);

  useEffect(() => {
    let mounted = true;
    const refresh = () => mounted && setOfflineMatches(offlineStorage.getOfflineMatches());

    offlineStorage.ready.then(refresh);
    const unsubscribe = offlineStorage.onChange(refresh);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const updateSyncStatus = () => {
      const status = syncService.getSyncStatus();
      setSyncStatus((prev) => ({
        ...prev,
        isSyncing: status.isSyncing,
        unsyncedCount: status.unsyncedCount,
        failedCount: status.failedCount,
        canSync: status.canSync
      }));
    };

    updateSyncStatus();

    const unsubscribeSync = syncService.onSyncStatusChange((status) => {
      setSyncStatus((prev) => ({
        ...prev,
        isSyncing: status.status === 'syncing',
        lastSyncMessage: status.message,
        syncError: status.status === 'error' ? status.message : null,
        syncErrors: status.errors || []
      }));

      if (status.status === 'completed' || status.status === 'partial' || status.status === 'error') {
        setTimeout(updateSyncStatus, 500);
      }
    });

    const interval = setInterval(updateSyncStatus, 5000);

    return () => {
      unsubscribeSync();
      clearInterval(interval);
    };
  }, []);

  // Always persist locally first so a dropped packet or killed tab cannot lose
  // the match. Then try the server briefly; if that is slow or fails, the local
  // copy is already durable and will sync when the connection is usable.
  const saveMatch = useCallback(async (matchData, isEdit = false, originalMatchId = null) => {
    const dataToSave = isEdit
      ? matchData
      : { ...matchData, clientId: matchData.clientId || offlineStorage.generateClientId() };

    await offlineStorage.ready;
    const localRecord = await persistMatchLocally(dataToSave, isEdit, originalMatchId);

    const canTryOnline = Boolean(token) && offlineStorage.isOnline();
    if (canTryOnline) {
      try {
        const onlineResult = await saveMatchOnline(dataToSave, isEdit, originalMatchId, token);
        await offlineStorage.dropSyncedMatch(localRecord.offlineId);
        return { success: true, data: onlineResult.data, source: 'online' };
      } catch (error) {
        console.error('Online save failed, keeping local copy for later sync:', error);
      }
    }

    return { success: true, data: localRecord, source: 'offline' };
  }, [token]);

  const triggerSync = useCallback(async () => {
    if (!token) {
      return { success: false, message: 'No authentication token' };
    }
    return syncService.triggerManualSync(token);
  }, [token]);

  const getOfflineMatches = useCallback(() => offlineStorage.getOfflineMatches(), []);
  const getStorageInfo = useCallback(() => offlineStorage.getStorageInfo(), []);

  return {
    isOnline,
    syncStatus,
    offlineMatches,
    saveMatch,
    triggerSync,
    getOfflineMatches,
    getStorageInfo
  };
};

const persistMatchLocally = async (matchData, isEdit, originalMatchId) => {
  if (isEdit && originalMatchId) {
    const existingOfflineMatch = offlineStorage
      .getOfflineMatches()
      .find((match) => match.serverId === originalMatchId || match.originalMatchId === originalMatchId);

    if (existingOfflineMatch) {
      return offlineStorage.updateOfflineMatch(existingOfflineMatch.offlineId, {
        ...matchData,
        originalMatchId
      });
    }
    return offlineStorage.saveMatchOffline({ ...matchData, originalMatchId });
  }

  const existingByClientId = matchData.clientId
    ? offlineStorage.findByClientId(matchData.clientId)
    : null;
  if (existingByClientId) {
    return offlineStorage.updateOfflineMatch(existingByClientId.offlineId, matchData);
  }

  return offlineStorage.saveMatchOffline(matchData);
};

const saveMatchOnline = async (matchData, isEdit, originalMatchId, token) => {
  if (!token) {
    throw new Error('No authentication token available');
  }

  let response;
  if (isEdit && originalMatchId) {
    const { clientId, ...updateBody } = matchData;
    response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/matches/${originalMatchId}`,
      updateBody,
      { headers: { Authorization: `Bearer ${token}` }, timeout: ONLINE_SAVE_TIMEOUT_MS }
    );
  } else {
    response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/matches`,
      matchData,
      { headers: { Authorization: `Bearer ${token}` }, timeout: ONLINE_SAVE_TIMEOUT_MS }
    );
  }

  return { success: true, data: response.data, source: 'online' };
};

export default useOffline;
